import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { addMobileDetectionListener } from '../utils/deviceDetection';
import '../App.css';
import loguardImg from '../loguard_img.png';
import dguardImg from '../dguard_img.png';
import dguardEyeImg from '../dguard_eye_img.png';
import uclmImg from '../uclm_img.png';

const SecurityPage = () => {
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState('uclm');
  const [isMobile, setIsMobile] = useState(false);

  const loguardRef = useRef(null);
  const uclmRef = useRef(null);
  const dguardRef = useRef(null);
  const dguardEyeRef = useRef(null);

  const categories = [
    { id: 'uclm', name: 'UCLM', title: 'UCLM' },
    { id: 'dguard', name: "D'Guard", title: "D'Guard" },
    { id: 'dguard-eye', name: "D'Guard EYE", title: "D'Guard EYE" },
    { id: 'loguard', name: 'LOGuard', title: 'LOGuard' },
  ];

  const refs = {
    loguard: loguardRef,
    uclm: uclmRef,
    dguard: dguardRef,
    'dguard-eye': dguardEyeRef,
  };

  useEffect(() => {
    const cleanupMobileListener = addMobileDetectionListener(setIsMobile);
    return () => {
      cleanupMobileListener();
    };
  }, []);

  // 스크롤 위치에 맞춰 좌측 카테고리 활성 상태 동기화
  useEffect(() => {
    const handleScroll = () => {
      const headerOffset = isMobile ? 70 : 88;
      const activationLine = headerOffset + 40;

      // activationLine(헤더 아래 기준선)보다 위에 있는 섹션 중
      // "가장 가까운(가장 아래에 있는)" 섹션을 활성 탭으로 선택
      let currentCategory = null;
      let bestTop = -Infinity;

      categories.forEach((category) => {
        const targetRef = refs[category.id];
        if (!targetRef?.current) return;

        const sectionTop = targetRef.current.getBoundingClientRect().top;
        if (sectionTop <= activationLine && sectionTop > bestTop) {
          currentCategory = category.id;
          bestTop = sectionTop;
        }
      });

      if (!currentCategory) currentCategory = 'uclm';

      setActiveCategory((prev) => (prev === currentCategory ? prev : currentCategory));
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile]);

  // 쿼리스트링으로 들어온 category에 맞게 초기 섹션 이동
  useEffect(() => {
    window.scrollTo(0, 0);

    const params = new URLSearchParams(location.search);
    const category = params.get('category');
    if (category && categories.find(cat => cat.id === category)) {
      setActiveCategory(category);
      setTimeout(() => {
        const targetRef = refs[category];
        if (targetRef && targetRef.current) {
          const headerOffset = isMobile ? 70 : 88;
          const elementPosition = targetRef.current.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      }, 100);
    } else {
      setActiveCategory('uclm');
    }
  }, [location.search, isMobile]);

  const handleCategoryClick = (categoryId) => {
    setActiveCategory(categoryId);
    const targetRef = refs[categoryId];
    if (targetRef && targetRef.current) {
      const headerOffset = isMobile ? 70 : 88;
      const elementPosition = targetRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className={`subcustom-header-container ${isMobile ? 'mobile-device' : ''}`}>
      <header className="subcustom-header">
        <div className="hero-title-area">
          <div className="hero-subtitle">SECURITY</div>
          <h2 className="hero-title">보안솔루션</h2>
        </div>
      </header>

      <main className="infra-main">
        <div className="solutions-layout">
          {/* 왼쪽 사이드바 카테고리 메뉴 - 데스크톱에서만 표시 */}
          {!isMobile && (
            <div className="sidebar-categories">
              <h3 className="sidebar-title">보안솔루션</h3>
              <ul className="category-menu">
                {categories.map(category => (
                  <li key={category.id}>
                    <button
                      className={`category-item ${activeCategory === category.id ? 'active' : ''}`}
                      onClick={() => handleCategoryClick(category.id)}
                    >
                      {category.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="content-area">
            {/* UCLM 섹션 */}
            <div className="solution-section" ref={uclmRef}>
              <h2 className="section-title">UCLM v1.0</h2>
              <div className="product-content">
                <div className="product-text">
                  <p>SSL/TSL 자동화 관리 솔루션</p>
                  <div className="feature-list">
                    <h3>주요 특징:</h3>
                    <ul>
                      <li>한국기업보안 제조사 공식 파트너</li>
                      <li>SSL/TSL 자동화 관리 솔루션</li>
                      <li>인증서 최대 유효기간 47일 단축에 선제 대응</li>
                      <li>서버·웹서비스·WAS·L4·방화벽 등의 인증서 자동 탐지</li>
                      <li>인증서 발급·갱신·배포 등 수명주기 전 과정 자동화</li>
                      <li>다양한 CA 및 사설 인증서를 중앙에서 통합 관리</li>
                      <li>GS인증(1등급) / 나라장터 디지털서비스몰 등록</li>
                    </ul>
                  </div>
                </div>
                <div className="product-image">
                  <img src={uclmImg} alt="UCLM" />
                </div>
              </div>
            </div>

            {/* D'Guard 섹션 */}
            <div className="solution-section" ref={dguardRef}>
              <h2 className="section-title">D'Guard v5.0</h2>
              <div className="product-content">
                <div className="product-text">
                  <p>DB 암호화 솔루션</p>
                  <div className="feature-list">
                    <h3>주요 특징:</h3>
                    <ul>
                      <li>아이넵 제조사 공식 파트너</li>
                      <li>국내 최초 자동 비정형데이터 암호화 지원 및 암호화 컬럼 외 개인정보 유출 점검 서비스 제공</li>
                      <li>실시간 로그파일, 대외 송수신 파일 실시간 암호화 등 다양한 유형의 실시간 암호화</li>
                      <li>Java 환경의 API 암호 모듈은 JNI 형태가 아닌 순수한 Java로 구현된 Crypto 모듈</li>
                      <li>다양한 OS 지원: AIX, HP-UX, Linux, Windows, Solaris 등</li>
                      <li>국제CC(PP) 인증 제품</li>
                      <li>GS인증(1등급) / 나라장터 디지털서비스몰 등록</li>
                    </ul>
                  </div>
                </div>
                <div className="product-image">
                  <img src={dguardImg} alt="D'Guard" />
                </div>
              </div>
            </div>

            {/* D'Guard EYE 섹션 */}
            <div className="solution-section" ref={dguardEyeRef}>
              <h2 className="section-title">D'Guard EYE v2.0</h2>
              <div className="product-content">
                <div className="product-text">
                  <p>서버 개인정보 검출 및 마스킹 솔루션</p>
                  <div className="feature-list">
                    <h3>주요 특징:</h3>
                    <ul>
                      <li>아이넵 제조사 공식 파트너</li>
                      <li>국내 최초 서버 개인정보 검출·마스킹 및 모니터링 기술 통합 제공</li>
                      <li>대용량 서버 검출·모니터링, 오탐·과탐 솔루션, 개인정보 마스킹</li>
                      <li>다양한 DBMS 검출 지원 (Oracle, MS-SQL 등)</li>
                      <li>서버 Agent 방식, Agentless 방식 제공</li>
                      <li>국가정보원 검증필 암호모듈 사용</li>
                      <li>GS인증(1등급) / 나라장터 디지털서비스몰 등록</li>
                    </ul>
                  </div>
                </div>
                <div className="product-image">
                  <img src={dguardEyeImg} alt="D'Guard EYE" />
                </div>
              </div>
            </div>

            {/* LOGuard 섹션 */}
            <div className="solution-section" ref={loguardRef}>
              <h2 className="section-title">LOGuard v3.0</h2>
              <div className="product-content">
                <div className="product-text">
                  <p>실시간 로그파일 암호화 솔루션</p>
                  <div className="feature-list">
                    <h3>주요 특징:</h3>
                    <ul>
                      <li>아이넵 제조사 공식 파트너</li>
                      <li>국내 최초 실시간 로그 및 파일 암호화 제공</li>
                      <li>실시간 암호화, 패턴 암호화, API 암호화, Filter 암호화 제공</li>
                      <li>서버 안정성을 위해 CPU 자원 보호 기술 탑재</li>
                      <li>다양한 OS 지원 (Unix, Linux, Window 등)</li>
                      <li>국정원 검증필 암호모듈 탑재</li>
                      <li>GS인증(1등급) / 나라장터 디지털서비스몰 등록</li>
                    </ul>
                  </div>
                </div>
                <div className="product-image">
                  <img src={loguardImg} alt="LOGuard" />
                </div>
              </div>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
};

export default SecurityPage;
